import React, { useEffect, useRef, useState } from 'react';
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { CollegeAccount, CollegeStudent } from '../types';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

function sendWelcomeEmail(studentEmail: string, studentName: string, collegeName: string) {
  httpsCallable(
    getFunctions(),
    'sendStudentWelcomeEmail'
  )({ studentEmail, studentName, collegeName }).catch(() => {});
}

function sendAdminWelcomeEmail(adminEmail: string, collegeName: string) {
  httpsCallable(
    getFunctions(),
    'sendAdminWelcomeEmail'
  )({ adminEmail, collegeName }).catch(() => {});
}

type Tab = 'csvWhitelisted' | 'csvActivated' | 'domainActivated';

interface DomainProfile {
  uid: string;
  name: string;
  authEmail: string;
}

export function CollegeAdminDashboard() {
  const { collegeAdmin } = useAuth();
  const { accountId, account: initialAccount, isPrimaryAdmin } = collegeAdmin!;

  const [account, setAccount] = useState<CollegeAccount>(initialAccount);
  const [students, setStudents] = useState<CollegeStudent[]>([]);
  const [domainProfiles, setDomainProfiles] = useState<DomainProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('domainActivated');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const studentListRef = useRef<HTMLDivElement>(null);

  const focusTab = (tab: Tab) => {
    setActiveTab(tab);
    setPage(1);
    setTimeout(() => studentListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // Edit contact info
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    contactName: initialAccount.contactName ?? '',
    contactPhone: initialAccount.contactPhone ?? '',
    website: initialAccount.website ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Add individual student
  const [addOpen, setAddOpen] = useState(false);
  const [newStudent, setNewStudent] = useState({ email: '', name: '' });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  // Team management
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminError, setAdminError] = useState('');

  // Query errors
  const [studentsError, setStudentsError] = useState('');
  const [domainError, setDomainError]     = useState('');

  // CSV upload
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ added: number; skipped: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    setStudentsError('');
    setDomainError('');
    await Promise.allSettled([
      getDocs(query(collection(db, 'collegeStudents'), where('accountId', '==', accountId)))
        .then((snap) => setStudents(snap.docs.map((d) => d.data() as CollegeStudent)))
        .catch(() => setStudentsError("Couldn't load student list.")),
      getDocs(query(collection(db, 'profiles'), where('premiumAccountId', '==', accountId)))
        .then((snap) => setDomainProfiles(snap.docs.map((d) => ({
          uid: d.id,
          name: d.data().name ?? d.data().displayName ?? '',
          authEmail: d.data().authEmail ?? '',
        }))))
        .catch(() => setDomainError("Couldn't load domain-activated users.")),
    ]);
    setLoading(false);
  };

  const handleSaveContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      await updateDoc(doc(db, 'collegeAccounts', accountId), {
        contactName: editForm.contactName,
        contactPhone: editForm.contactPhone,
        website: editForm.website,
        updatedAt: serverTimestamp(),
      });
      setAccount((prev) => ({ ...prev, ...editForm }));
      setSaveSuccess(true);
      setEditOpen(false);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newStudent.email.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    setAdding(true);
    setAddError('');
    try {
      const batch = writeBatch(db);
      batch.set(doc(db, 'collegeStudents', email), {
        email,
        name: newStudent.name.trim(),
        graduationYear: null,
        accountId,
        accountName: account.name,
        activated: false,
        addedAt: serverTimestamp(),
      });
      await batch.commit();
      sendWelcomeEmail(email, newStudent.name.trim(), account.name);
      setNewStudent({ email: '', name: '' });
      setAddOpen(false);
      await loadData();
    } catch (err: any) {
      setAddError(err.message || 'Failed to add student.');
    } finally {
      setAdding(false);
    }
  };

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      const existingEmails = new Set(students.map((s) => s.email.toLowerCase()));
      const newRows = rows.filter((r) => {
        const em = r.email.toLowerCase().trim();
        return em && em.includes('@') && !existingEmails.has(em);
      });
      let added = 0;
      for (let i = 0; i < newRows.length; i += 499) {
        const chunk = newRows.slice(i, i + 499);
        const batch = writeBatch(db);
        for (const { email, name, graduationYear } of chunk) {
          const emailKey = email.toLowerCase().trim();
          batch.set(doc(db, 'collegeStudents', emailKey), {
            email: emailKey,
            name: name.trim(),
            graduationYear: graduationYear.trim() || null,
            accountId,
            accountName: account.name,
            activated: false,
            addedAt: serverTimestamp(),
          });
          added++;
        }
        await batch.commit();
      }
      setUploadResult({ added, skipped: rows.length - added });
      newRows.forEach(({ email, name }) =>
        sendWelcomeEmail(email.toLowerCase().trim(), name.trim(), account.name)
      );
      await loadData();
    } catch (err: any) {
      alert('CSV upload failed: ' + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newAdminEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    setAdminError('');
    setAdminSaving(true);
    try {
      await updateDoc(doc(db, 'collegeAccounts', accountId), {
        adminEmails: arrayUnion(email),
        updatedAt: serverTimestamp(),
      });
      setAccount((prev) => ({
        ...prev,
        adminEmails: [...(prev.adminEmails ?? []), email],
      }));
      setNewAdminEmail('');
      sendAdminWelcomeEmail(email, account.name);
    } catch (err: any) {
      setAdminError(err.message || 'Failed to add admin.');
    } finally {
      setAdminSaving(false);
    }
  };

  const handleRemoveAdmin = async (email: string) => {
    setAdminError('');
    setAdminSaving(true);
    try {
      await updateDoc(doc(db, 'collegeAccounts', accountId), {
        adminEmails: arrayRemove(email),
        updatedAt: serverTimestamp(),
      });
      setAccount((prev) => ({
        ...prev,
        adminEmails: (prev.adminEmails ?? []).filter((e) => e !== email),
      }));
    } catch (err: any) {
      setAdminError(err.message || 'Failed to remove admin.');
    } finally {
      setAdminSaving(false);
    }
  };

  const handleRemoveStudent = async (email: string) => {
    if (!window.confirm(`Remove ${email} from the whitelist? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'collegeStudents', email));
      setStudents((prev) => prev.filter((s) => s.email !== email));
    } catch (err: any) {
      alert(err.message || 'Failed to remove student.');
    }
  };

  const csvWhitelisted = students;
  const csvActivated = students
    .filter((s) => s.activated)
    .sort((a, b) => {
      const at = a.activatedAt ? (a.activatedAt as Timestamp).toMillis() : 0;
      const bt = b.activatedAt ? (b.activatedAt as Timestamp).toMillis() : 0;
      return bt - at;
    });

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  const memberLabel = account.type === 'college' ? 'Student' : 'Member';

  return (
    <Layout>
      <div style={{ maxWidth: 960 }}>

        {/* Header */}
        <div className="page-header" style={{ marginBottom: 24 }}>
          <div>
            <h1 className="page-title">{account.name}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <span className={`badge badge-${account.type}`}>
                {account.type === 'college' ? 'College' : 'Alumni Association'}
              </span>
              <span className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
                {cap(account.plan)} plan
              </span>
            </div>
          </div>
          <button className="btn-secondary" onClick={() => {
            setEditForm({
              contactName: account.contactName ?? '',
              contactPhone: account.contactPhone ?? '',
              website: account.website ?? '',
            });
            setSaveSuccess(false);
            setSaveError('');
            setEditOpen((o) => !o);
          }}>
            {editOpen ? 'Cancel' : 'Edit College Info'}
          </button>
        </div>

        {/* Edit contact info */}
        {editOpen && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              Edit College Info
            </div>
            {saveError && <div className="error-message" style={{ marginBottom: 14 }}>{saveError}</div>}
            <form onSubmit={handleSaveContact}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Contact Name</label>
                  <input
                    className="form-input"
                    value={editForm.contactName}
                    onChange={(e) => setEditForm((f) => ({ ...f, contactName: e.target.value }))}
                    placeholder="Your name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    className="form-input"
                    value={editForm.contactPhone}
                    onChange={(e) => setEditForm((f) => ({ ...f, contactPhone: e.target.value }))}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input
                    className="form-input"
                    value={editForm.website}
                    onChange={(e) => setEditForm((f) => ({ ...f, website: e.target.value }))}
                    placeholder="https://"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setEditOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {saveSuccess && !editOpen && (
          <div className="success-message" style={{ marginBottom: 16 }}>
            College info updated successfully.
          </div>
        )}

        {/* Stats */}
        {studentsError && (
          <div className="error-message" style={{ marginBottom: 12 }}>{studentsError}</div>
        )}
        {domainError && (
          <div className="error-message" style={{ marginBottom: 12 }}>{domainError}</div>
        )}
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12, marginTop: 0 }}>
          Track how students are accessing Klarr Coach through your account.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
            marginBottom: 16,
          }}
        >
          {([
            { key: 'csvWhitelisted',  label: 'CSV Whitelisted',  value: csvWhitelisted.length,  desc: 'Students added via CSV upload',                              accent: false },
            { key: 'csvActivated',    label: 'CSV Activated',    value: csvActivated.length,    desc: 'Whitelisted students who have signed into Klarr',           accent: true  },
            { key: 'domainActivated', label: 'Domain Activated', value: domainProfiles.length,  desc: 'Students who signed up using your college email domain',    accent: true  },
          ] as { key: Tab; label: string; value: number; desc: string; accent: boolean }[]).map(({ key, label, value, desc, accent }) => (
            <button
              key={key}
              className="stat-card"
              onClick={() => focusTab(key)}
              style={{ cursor: 'pointer', textAlign: 'left', width: '100%', background: 'none', border: '1px solid var(--border)', display: 'block' }}
            >
              <div className="stat-label">{label}</div>
              <div className="stat-value" style={accent ? { color: 'var(--accent-text)' } : undefined}>{value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.4 }}>{desc}</div>
            </button>
          ))}
        </div>

        {/* Account details (read-only) */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
            Account Details
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
            <Row label="Plan" value={cap(account.plan)} />
            <Row label={`${memberLabel} Limit`} value={account.studentLimit?.toLocaleString()} />
            <Row label="Domain Activation" value={account.domainEnabled ? `Enabled — ${account.domain || 'no domain set'}` : 'Disabled'} />
            <Row label="CSV Upload" value={account.csvEnabled ? 'Enabled' : 'Disabled'} />
            <Row label="Start Date" value={fmtDate(account.startDate)} />
            <Row label="Expiry Date" value={fmtDate(account.expiryDate)} />
            {account.city && <Row label="Location" value={`${account.city}, ${account.state}`} />}
            {account.contactName && <Row label="Contact" value={account.contactName} />}
            {account.website && (
              <Row label="Website" value={<a href={account.website} target="_blank" rel="noreferrer">{account.website}</a>} />
            )}
          </div>
        </div>

        {/* Team */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            Team
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, marginTop: 0 }}>
            Manage who can access this college's Klarr admin portal.
          </p>
          {/* Primary admin */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{account.contactEmail}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Primary Admin</div>
            </div>
          </div>
          {/* Additional admins */}
          {(account.adminEmails ?? []).map((email) => (
            <div key={email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{email}</div>
              {isPrimaryAdmin && (
                <button
                  className="btn-secondary"
                  style={{ padding: '3px 10px', fontSize: 12 }}
                  disabled={adminSaving}
                  onClick={() => handleRemoveAdmin(email)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          {/* Add admin form — primary only */}
          {isPrimaryAdmin && (
            <form onSubmit={handleAddAdmin} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 14 }}>
              <div className="form-group" style={{ margin: 0, flex: 1 }}>
                <label className="form-label">Add Admin Email</label>
                <input
                  className="form-input"
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => { setNewAdminEmail(e.target.value); setAdminError(''); }}
                  placeholder="colleague@university.edu"
                />
              </div>
              <button
                type="submit"
                className="btn-primary"
                disabled={adminSaving || !newAdminEmail.trim()}
                style={{ flexShrink: 0 }}
              >
                {adminSaving ? 'Saving...' : 'Add'}
              </button>
            </form>
          )}
          {adminError && <div className="error-message" style={{ marginTop: 10 }}>{adminError}</div>}
        </div>

        {/* CSV Upload */}
        {account.csvEnabled && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Upload CSV</h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Upload a CSV with columns: <code>email</code>, <code>name</code>, <code>graduationYear</code> —
              name and graduationYear are optional. An optional header row is ignored. Existing emails are skipped.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleCSVUpload}
              style={{ display: 'none' }}
            />
            <button
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </button>
            {uploadResult && (
              <div className="success-message" style={{ marginTop: 14 }}>
                Added <strong>{uploadResult.added}</strong> new email
                {uploadResult.added !== 1 ? 's' : ''}.
                {uploadResult.skipped > 0 && ` ${uploadResult.skipped} skipped (duplicates or invalid).`}
              </div>
            )}
          </div>
        )}

        {/* Student list */}
        <div className="card" ref={studentListRef} style={{ padding: 0 }}>
          {/* Search */}
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
            <input
              className="form-input"
              type="search"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ maxWidth: 320, margin: 0 }}
            />
          </div>

          {/* Tab bar + Add button */}
          <div
            style={{
              padding: '14px 24px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', gap: 4 }}>
              {([
                { key: 'csvWhitelisted', label: `CSV Whitelisted (${csvWhitelisted.length})` },
                { key: 'csvActivated', label: `CSV Activated (${csvActivated.length})` },
                { key: 'domainActivated', label: `Domain Activated (${domainProfiles.length})` },
              ] as { key: Tab; label: string }[]).map(({ key, label }) => (
                <button
                  key={key}
                  className={`filter-btn ${activeTab === key ? 'active' : ''}`}
                  onClick={() => { setActiveTab(key); setPage(1); }}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              className="btn-secondary"
              style={{ padding: '5px 14px', fontSize: 13, flexShrink: 0 }}
              onClick={() => { setAddOpen((o) => !o); setAddError(''); }}
            >
              {addOpen ? 'Cancel' : `+ Add ${memberLabel}`}
            </button>
          </div>

          {/* Add student inline form */}
          {addOpen && (
            <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
              {addError && <div className="error-message" style={{ marginBottom: 12 }}>{addError}</div>}
              <form onSubmit={handleAddStudent} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ margin: 0, flex: '1 1 200px' }}>
                  <label className="form-label">Email *</label>
                  <input
                    className="form-input"
                    type="email"
                    value={newStudent.email}
                    onChange={(e) => setNewStudent((s) => ({ ...s, email: e.target.value }))}
                    placeholder="student@university.edu"
                    required
                  />
                </div>
                <div className="form-group" style={{ margin: 0, flex: '1 1 160px' }}>
                  <label className="form-label">Name</label>
                  <input
                    className="form-input"
                    value={newStudent.name}
                    onChange={(e) => setNewStudent((s) => ({ ...s, name: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={adding} style={{ flexShrink: 0 }}>
                  {adding ? 'Adding...' : `Add ${memberLabel}`}
                </button>
              </form>
            </div>
          )}

          {/* Tab content */}
          {activeTab === 'csvWhitelisted' && (
            <StudentTable
              rows={csvWhitelisted.map((s) => ({
                email: s.email, name: s.name,
                sub: s.activated ? 'Activated' : 'Not yet activated',
                graduationYear: s.graduationYear,
                source: 'CSV',
              }))}
              emptyMessage={`No ${memberLabel.toLowerCase()}s whitelisted yet.`}
              onRemove={handleRemoveStudent}
              search={search}
              page={page}
              onPageChange={setPage}
            />
          )}

          {activeTab === 'csvActivated' && (
            <StudentTable
              rows={csvActivated.map((s) => ({
                email: s.email, name: s.name,
                sub: s.activatedAt ? fmtTimestamp(s.activatedAt as Timestamp) : '',
                graduationYear: s.graduationYear,
                source: 'CSV',
              }))}
              emptyMessage={`No ${memberLabel.toLowerCase()}s have activated yet.`}
              search={search}
              page={page}
              onPageChange={setPage}
            />
          )}

          {activeTab === 'domainActivated' && (
            <StudentTable
              rows={domainProfiles.map((p) => ({
                email: p.authEmail, name: p.name, sub: '',
                graduationYear: null,
                source: 'Domain',
              }))}
              emptyMessage="No users have activated via domain yet."
              search={search}
              page={page}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

const PAGE_SIZE = 25;

function StudentTable({
  rows,
  emptyMessage,
  onRemove,
  search,
  page,
  onPageChange,
}: {
  rows: { email: string; name: string; sub: string; graduationYear?: string | null; source?: string }[];
  emptyMessage: string;
  onRemove?: (email: string) => void;
  search: string;
  page: number;
  onPageChange: (page: number) => void;
}) {
  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) => r.email.toLowerCase().includes(q) || r.name.toLowerCase().includes(q))
    : rows;

  if (filtered.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
        {q ? `No results for "${search}".` : emptyMessage}
      </div>
    );
  }

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);

  return (
    <>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Grad Year</th>
              <th>Status / Date</th>
              <th>Source</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.email}>
                <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{r.email || '—'}</td>
                <td style={{ color: r.name ? undefined : 'var(--text-muted)' }}>{r.name || '—'}</td>
                <td style={{ fontSize: 13, color: r.graduationYear ? undefined : 'var(--text-muted)' }}>
                  {r.graduationYear || '—'}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.sub || '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.source || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {onRemove && (
                    <button
                      className="btn-secondary"
                      style={{ padding: '2px 10px', fontSize: 12 }}
                      onClick={() => onRemove(r.email)}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length > PAGE_SIZE && (
        <div
          style={{
            padding: '12px 24px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Showing {start + 1}–{Math.min(start + PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn-secondary"
              style={{ padding: '5px 14px', fontSize: 13 }}
              disabled={safePage === 1}
              onClick={() => onPageChange(safePage - 1)}
            >
              Previous
            </button>
            <button
              className="btn-secondary"
              style={{ padding: '5px 14px', fontSize: 13 }}
              disabled={safePage === totalPages}
              onClick={() => onPageChange(safePage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, gap: 16 }}>
      <span style={{ fontSize: 13, color: 'var(--text-secondary)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text-primary)', textAlign: 'right' }}>{value ?? '—'}</span>
    </div>
  );
}

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtDate(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function fmtTimestamp(ts: Timestamp) {
  return ts.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function parseCSV(text: string): { email: string; name: string; graduationYear: string }[] {
  const results: { email: string; name: string; graduationYear: string }[] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  for (const line of lines) {
    const parts = line.split(',').map((p) => p.trim().replace(/^"(.*)"$/, '$1').trim());
    if (parts.length < 1) continue;
    const [first, second = '', third = ''] = parts;
    if (first.toLowerCase() === 'email') continue;
    if (first.includes('@')) results.push({ email: first, name: second, graduationYear: third });
  }
  return results;
}
